import { z } from 'zod'

export const ROLES = ['reviewer', 'approver', 'admin'] as const
export const Role = z.enum(ROLES)
export type Role = z.infer<typeof Role>

export const PLATFORMS = ['facebook', 'instagram'] as const
export const Platform = z.enum(PLATFORMS)
export type Platform = z.infer<typeof Platform>

export const CONVERSATION_KINDS = ['comment_thread', 'dm'] as const
export const ConversationKind = z.enum(CONVERSATION_KINDS)
export type ConversationKind = z.infer<typeof ConversationKind>

export const CONVERSATION_STATUSES = ['open', 'replied', 'withdrawn'] as const
export const ConversationStatus = z.enum(CONVERSATION_STATUSES)
export type ConversationStatus = z.infer<typeof ConversationStatus>

export const FOLDERS = ['inbox', 'spam', 'muted'] as const
export const Folder = z.enum(FOLDERS)
export type Folder = z.infer<typeof Folder>

export const INTENTS = ['question', 'complaint', 'sales_lead', 'praise', 'spam', 'troll', 'reaction', 'other'] as const
export const Intent = z.enum(INTENTS)
export type Intent = z.infer<typeof Intent>

export const LANGUAGES = ['yue', 'cmn', 'en', 'mixed', 'other'] as const
export const Language = z.enum(LANGUAGES)
export type Language = z.infer<typeof Language>

export const CLASSIFICATION_STATUSES = ['pending', 'done', 'failed'] as const
export const ClassificationStatus = z.enum(CLASSIFICATION_STATUSES)
export type ClassificationStatus = z.infer<typeof ClassificationStatus>

export const CLASSIFICATION_SOURCES = ['rule', 'llm', 'human'] as const
export const ClassificationSource = z.enum(CLASSIFICATION_SOURCES)
export type ClassificationSource = z.infer<typeof ClassificationSource>

export const SUGGESTION_MODES = ['auto', 'manual'] as const
export const SuggestionMode = z.enum(SUGGESTION_MODES)
export type SuggestionMode = z.infer<typeof SuggestionMode>

export const SUGGESTION_STATUSES = ['pending', 'ready', 'no_confident', 'failed', 'stale'] as const
export const SuggestionStatus = z.enum(SUGGESTION_STATUSES)
export type SuggestionStatus = z.infer<typeof SuggestionStatus>

export const AUTO_SUGGESTION_STATES = ['queued', 'manual', 'not_inbox', 'unclassified', 'out_of_domain', 'suppressed_intent'] as const
export const AutoSuggestionState = z.enum(AUTO_SUGGESTION_STATES)
export type AutoSuggestionState = z.infer<typeof AutoSuggestionState>

export const SUGGESTION_OUTCOMES = ['as_is', 'light_edit', 'heavy_edit', 'ignored'] as const
export const SuggestionOutcome = z.enum(SUGGESTION_OUTCOMES)
export type SuggestionOutcome = z.infer<typeof SuggestionOutcome>

export const REPLY_STATUSES = ['publishing', 'published', 'failed'] as const
export const ReplyStatus = z.enum(REPLY_STATUSES)
export type ReplyStatus = z.infer<typeof ReplyStatus>

export const CHANNEL_STATUSES = ['connected', 'disconnected'] as const
export const ChannelStatus = z.enum(CHANNEL_STATUSES)
export type ChannelStatus = z.infer<typeof ChannelStatus>

export const ATTACHMENT_KINDS = ['image', 'sticker', 'video', 'audio', 'file', 'other'] as const
export const AttachmentKind = z.enum(ATTACHMENT_KINDS)
export type AttachmentKind = z.infer<typeof AttachmentKind>

export const KB_SOURCE_STATUSES = ['pending', 'ingesting', 'ready', 'failed'] as const
export const KbSourceStatus = z.enum(KB_SOURCE_STATUSES)
export type KbSourceStatus = z.infer<typeof KbSourceStatus>

export const CORPUS_STATUSES = ['active', 'retired'] as const
export const CorpusStatus = z.enum(CORPUS_STATUSES)
export type CorpusStatus = z.infer<typeof CorpusStatus>

export const PROVIDER_IDS = ['kimi', 'vertex_gemini', 'azure_openai', 'typesafe_jev', 'anthropic_claude', 'fake'] as const
export const ProviderId = z.enum(PROVIDER_IDS)
export type ProviderId = z.infer<typeof ProviderId>

export const DECISION_PROVIDER_IDS = ['typesafe_jev'] as const satisfies readonly ProviderId[]
export const CHAT_PROVIDER_IDS = ['kimi', 'vertex_gemini', 'azure_openai', 'anthropic_claude', 'fake'] as const satisfies readonly ProviderId[]
export const ChatProviderId = z.enum(CHAT_PROVIDER_IDS)
export type ChatProviderId = z.infer<typeof ChatProviderId>

export const OFFERED_CHAT_PROVIDER_IDS = ['vertex_gemini', 'anthropic_claude'] as const satisfies readonly ChatProviderId[]
export const KEY_REQUIRED_CHAT_PROVIDER_IDS: readonly ProviderId[] = ['anthropic_claude']

export const PROVIDER_CIRCUIT_STATES = ['closed', 'open', 'half_open'] as const
export const ProviderCircuitState = z.enum(PROVIDER_CIRCUIT_STATES)
export type ProviderCircuitState = z.infer<typeof ProviderCircuitState>

export const MESSAGE_DIRECTIONS = ['inbound', 'outbound'] as const
export const MessageDirection = z.enum(MESSAGE_DIRECTIONS)
export type MessageDirection = z.infer<typeof MessageDirection>

export const SPAM_RULE_KINDS = ['keyword', 'domain', 'pattern'] as const
export const SpamRuleKind = z.enum(SPAM_RULE_KINDS)
export type SpamRuleKind = z.infer<typeof SpamRuleKind>

export const KB_SOURCE_KINDS = ['upload', 'url'] as const
export const KbSourceKind = z.enum(KB_SOURCE_KINDS)
export type KbSourceKind = z.infer<typeof KbSourceKind>

export const CORPUS_ADMISSIONS = ['approver_post', 'endorsement'] as const
export const CorpusAdmission = z.enum(CORPUS_ADMISSIONS)
export type CorpusAdmission = z.infer<typeof CorpusAdmission>

export const CLAIM_ACTIONS = ['claim', 'release', 'takeover', 'expire'] as const
export const ClaimAction = z.enum(CLAIM_ACTIONS)
export type ClaimAction = z.infer<typeof ClaimAction>

export const USAGE_KINDS = ['ingested', 'classified', 'suggested', 'replied', 'llm_tokens', 'kb_chunks'] as const
export const UsageKind = z.enum(USAGE_KINDS)
export type UsageKind = z.infer<typeof UsageKind>

export const FAILURE_CODES = [
  'database_query_failed',
  'input_invalid',
  'request_failed',
  'unexpected_failure',
  'provider_unavailable',
  'provider_rejected',
  'provider_timeout',
  'provider_rate_limited',
  'meta_unavailable',
  'meta_rejected',
  'meta_timeout',
  'meta_auth',
  'meta_not_found',
  'meta_object_unavailable',
  'job_payload_invalid',
  'tenant_inactive',
  'conversation_not_in_tenant',
  'conversation_missing',
  'message_not_in_conversation',
  'message_not_recorded',
  'channel_not_in_tenant',
  'channel_token_missing',
  'channel_token_unreadable',
  'kb_source_not_in_tenant',
  'kb_source_url_blocked',
  'kb_source_unreadable',
  'kb_source_parse_incomplete',
  'kb_source_fetch_failed',
  'kb_source_upload_unusable',
  'suggestion_generation_failed',
  'budget_exhausted',
  'credentials_missing',
] as const
export const FailureCode = z.enum(FAILURE_CODES)
export type FailureCode = z.infer<typeof FailureCode>
